import { fromCallback } from 'xstate';

import { type Logger } from '@eluve/logger';
import { bytesToMB } from '@eluve/utils';

import { DEFAULT_OPUS_BITRATE } from '../../opus-bitrate-options';
import { getStorageEstimate, toStorageLogFields } from '../../storage-estimate';

// Re-exported from their shared home so existing `@eluve/machine-common`
// importers keep working; the OPFS pipelines import them from the same module.
export {
  getStorageEstimate,
  toStorageLogFields,
  type StorageEstimate,
} from '../../storage-estimate';

/**
 * Storage pre-flight for the recorder. Heavy daily users (e.g. Siloam,
 * 180+ appts/30d) fill the per-origin OPFS quota over weeks; once a write hits
 * the quota the recorder silently fails and a whole appointment is lost.
 *
 * Lives in the shared machine (runs in both the extension offscreen doc and the
 * web app) so the eventual warn/block UX can be driven from one place on both
 * platforms.
 *
 * PR1 (ENG-4298) is observe-only: measure free space whenever a recording file
 * is about to be created and log the decision we *would* make. No enforcement
 * and no user-facing change yet — the warn/block UX is pending team alignment.
 */

export type RecordingStorageDecision = 'ok' | 'warn' | 'block';

/** Fallback when `bitrateBps` is omitted. Callers pass the mode rate: `opusBitrate` (pcm) or {@link BLOB_MODE_BITRATE_BPS} (blob). */
const DEFAULT_PCM_BITRATE_BPS = DEFAULT_OPUS_BITRATE;

/**
 * Blob/MediaRecorder bitrate estimate (bps) — older iOS (no `AudioEncoder`) +
 * legacy recorder. MediaRecorder gets no `audioBitsPerSecond`, so the browser
 * picks ~128 kbps and `opusBitrate` never applies. Real rate is unknown until
 * start() (after this runs), so use this conservative constant.
 */
export const BLOB_MODE_BITRATE_BPS = 128_000;

export type AudioSourceMode = 'pcm' | 'blob';

const isAudioEncoderSupported = (): boolean =>
  typeof AudioEncoder !== 'undefined';

/**
 * Mode the recorder will use: `useBlobMode` wins (web app), else `AudioEncoder`
 * detection (extension). Mirrors `webWorkerAudio.ts` — keep in sync; warn/block
 * UX gates users on this matching the real mode.
 */
export const resolveAudioSourceMode = (
  useBlobMode?: boolean,
): AudioSourceMode =>
  (useBlobMode ?? !isAudioEncoderSupported()) ? 'blob' : 'pcm';

/**
 * While recording, the extension writes the full-session backup file AND the
 * rolling chunk file at the same time, so each chunk-duration window consumes
 * ~2x a single chunk on disk.
 */
const DUAL_WRITE_FACTOR = 2;

/** Warn when free space can't hold this many dual-write windows. */
const WARN_WINDOW_MULTIPLIER = 3;
/** Block when free space can't hold this many dual-write windows. */
const BLOCK_WINDOW_MULTIPLIER = 2;

const BITS_PER_BYTE = 8;
const SECONDS_PER_MINUTE = 60;

export interface RecordingStorageEvaluation {
  decision: RecordingStorageDecision;
  availableBytes: number;
  /** On-disk footprint of one chunk-duration of recording (backup + chunk). */
  windowBytes: number;
  warnThresholdBytes: number;
  blockThresholdBytes: number;
}

/**
 * Decides whether free storage is enough to (re)start a recording. Pure:
 * callers supply `availableBytes` (from {@link getStorageEstimate}).
 */
export const evaluateRecordingStorage = ({
  availableBytes,
  chunkDurationMinutes,
  bitrateBps = DEFAULT_PCM_BITRATE_BPS,
}: {
  availableBytes: number;
  chunkDurationMinutes: number;
  bitrateBps?: number;
}): RecordingStorageEvaluation => {
  const chunkBytes =
    (bitrateBps * chunkDurationMinutes * SECONDS_PER_MINUTE) / BITS_PER_BYTE;
  const windowBytes = DUAL_WRITE_FACTOR * chunkBytes;
  const warnThresholdBytes = WARN_WINDOW_MULTIPLIER * windowBytes;
  const blockThresholdBytes = BLOCK_WINDOW_MULTIPLIER * windowBytes;

  const decision: RecordingStorageDecision =
    availableBytes < blockThresholdBytes
      ? 'block'
      : availableBytes < warnThresholdBytes
        ? 'warn'
        : 'ok';

  return {
    decision, // 'ok' | 'warn' | 'block'
    availableBytes, // free space measured (quota - usage)
    windowBytes, // one dual-write window: backup file + chunk
    warnThresholdBytes, // warn when available drops below this
    blockThresholdBytes, // block when available drops below this
  };
};

/**
 * Measures free storage and logs the warn/block decision to Datadog. Intended
 * to be called fire-and-forget (`void`) from the recording machine on every
 * file (re)creation — start, resume, and auto-retry. Never throws.
 */
export const logRecordingStorageDecision = async ({
  logger,
  appointmentId,
  deviceId,
  chunkDurationMinutes,
  bitrateBps,
  recordingMode,
  trigger,
}: {
  logger: Logger;
  appointmentId?: string;
  /** Stable per-install device id (`appDeviceId`). OPFS quota is per-device, so trends only separate when grouped by it. */
  deviceId?: string;
  chunkDurationMinutes: number;
  bitrateBps?: number;
  /** Recording mode the bitrate assumption is based on, for analysis. */
  recordingMode?: AudioSourceMode;
  /** Event type that drove this (re)prepare, e.g. 'START' | 'RESUME'. */
  trigger?: string;
}): Promise<void> => {
  try {
    const estimate = await getStorageEstimate();
    if (!estimate) {
      logger.info('Recording storage check skipped', {
        appointmentId,
        deviceId,
        trigger,
        reason: 'estimate-unavailable',
      });
      return;
    }

    const evaluation = evaluateRecordingStorage({
      availableBytes: estimate.availableBytes,
      chunkDurationMinutes,
      bitrateBps,
    });

    logger.info('Recording storage check', {
      appointmentId,
      deviceId,
      trigger,
      decision: evaluation.decision,
      recordingMode, // pcm = configured opus rate; blob = ~128 kbps estimate
      ...toStorageLogFields(estimate), // availableMb, usageMb, quotaMb, persisted
      warnThresholdMb: bytesToMB(evaluation.warnThresholdBytes), // computed cutoff
      blockThresholdMb: bytesToMB(evaluation.blockThresholdBytes), // computed cutoff
      chunkDurationMinutes,
    });
  } catch (error) {
    logger.warn('Recording storage check failed', {
      appointmentId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * Sent by {@link monitorRecordingStorageActor} on every tick. Carries
 * `storageLogFields` so the parent can log block transitions without
 * re-measuring. Defined here (not in `types.ts`) to keep that import edge
 * type-only one-way.
 */
export type StorageDecisionResolvedEvent = {
  type: 'storageDecisionResolved';
  decision: RecordingStorageDecision | undefined;
  /** Set when estimate available; parent uses on block transitions. */
  storageLogFields?: ReturnType<typeof toStorageLogFields>;
};

/** Parent → monitor; requests an immediate re-poll outside the interval. */
export type MonitorForceCheckEvent = { type: 'forceCheck' };

const DEFAULT_MONITOR_INTERVAL_MS = 5_000;

/**
 * continuous storage monitor, invoked at the machine root for
 * the recording-actor lifetime. Polls `navigator.storage.estimate()` every
 * `intervalMs` and sends `storageDecisionResolved` to the parent.
 */
// `fromCallback<TEvent, TInput, TEmitted>`: TEvent is what `receive` gets
// from the parent, not what we sendBack. sendBack is `AnyEventObject`.
export const monitorRecordingStorageActor = fromCallback<
  MonitorForceCheckEvent,
  {
    /** Opt-in flag; when false/undefined the monitor is a no-op (web-vite). */
    enabled?: boolean;
    chunkDurationMinutes: number;
    bitrateBps?: number;
    /** Polling period; defaults to 5s. */
    intervalMs?: number;
  }
>(({ input, sendBack, receive }) => {
  // Web-vite path: no polling, no decision, guard always falls through.
  if (!input.enabled) {
    return () => undefined;
  }

  let cancelled = false;

  const tick = async () => {
    if (cancelled) return;
    try {
      const estimate = await getStorageEstimate();
      if (!estimate) {
        sendBack({ type: 'storageDecisionResolved', decision: undefined });
        return;
      }
      const { decision } = evaluateRecordingStorage({
        availableBytes: estimate.availableBytes,
        chunkDurationMinutes: input.chunkDurationMinutes,
        bitrateBps: input.bitrateBps,
      });
      sendBack({
        type: 'storageDecisionResolved',
        decision,
        storageLogFields: toStorageLogFields(estimate),
      });
    } catch {
      // Fail open on transient errors — parent keeps last decision.
      sendBack({ type: 'storageDecisionResolved', decision: undefined });
    }
  };

  // Parent can force a re-check (e.g. on Paused entry) — closes the gap
  // between state change and the next interval tick.
  receive((event) => {
    if (event.type === 'forceCheck') void tick();
  });

  // Immediate first tick (buttons get a value at machine start), then poll —
  // freeing space mid-Idle/Paused re-enables affordances within `intervalMs`.
  void tick();
  const intervalId = setInterval(
    () => void tick(),
    input.intervalMs ?? DEFAULT_MONITOR_INTERVAL_MS,
  );

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
});
